import React, { useCallback, useMemo } from 'react';
import { format, isThisYear } from 'date-fns';
import { ActivityIndicator, View } from 'react-native';
import { Button, CardView, rem, Separator, Spacer } from 'design-system';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppSelector } from 'store/hooks';
import LogoBankAccount from 'design-system/LogoBankAccount';
import Text from 'design-system/Text';
import Status from 'design-system/Status';
import BlurAmountView from 'design-system/BlurAmountView';
import { formatSortCodeAndAccountNumber } from 'utils';
import IconCircle from 'design-system/icons/IconCircle';
import IconSpace from 'design-system/icons/IconSpace';
import useColors from 'hooks/useColors';
import { selectHasSpaces, selectSpaceInfo } from 'features/spaces/reducers/selectors';
import RentReportingStatus from 'features/rent-reporting/components/RentReportingStatus';
import { selectRentReportingForAccount } from 'features/rent-reporting/selectors';
import ManageAccountModal from './ManageAccountModal';
import { statusStrings } from '../utils';
export const BankConnectionAccount = ({ account, isConnectionClosed, isConnectionDeactivated, toggleClosed, toggleHidden, }) => {
    const styles = useStyles(styleSet);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const savingsPlanAccountId = useAppSelector((state) => state.pots.savingsPlan?.bankInfo?.accountId);
    const isAccountLinkedToAutosave = savingsPlanAccountId === account.id;
    const colors = useColors();
    const spaceInfoRecord = useAppSelector(selectSpaceInfo);
    const hasSpaces = useAppSelector(selectHasSpaces);
    const rentReporting = useAppSelector((store) => selectRentReportingForAccount(store, account.id));
    const rows = useMemo(() => {
        const arr = [];
        const space = spaceInfoRecord[account.spaceId]?.space;
        const spaceName = space?.isDefault ? 'Private space' : space?.name;
        if (hasSpaces && spaceName) {
            arr.push({
                title: 'Space',
                value: (<View style={styles.container}>
            <IconCircle style={styles.iconCircle} color={colors.background.brandLight}>
              <IconSpace scale={0.6} color={colors.elements.brand} style={styles.iconSpace}/>
            </IconCircle>
            <Spacer.W8 />
            <Text Text-14>{spaceName}</Text>
          </View>),
            });
        }
        arr.push({
            title: 'Last synced',
            value: (<Text Text-14>
          {account.lastSyncAt
                    ? format(account.lastSyncAt, isThisYear(account.lastSyncAt) ? 'd MMM h:mma' : 'd MMM yyyy h:mma')
                    : '—'}
        </Text>),
        });
        if (isAccountLinkedToAutosave) {
            arr.push({
                title: 'Autosave',
                value: <Status activeText="Linked" inactiveText="Linked" active/>,
            });
        }
        if (rentReporting) {
            arr.push({
                title: 'Rent reporting',
                value: (<RentReportingStatus active={Boolean(rentReporting.reportingAgencies?.length && rentReporting.isTracking)}/>),
            });
        }
        if (account.isClosed && !isConnectionClosed && !isConnectionDeactivated) {
            arr.push({
                title: 'Status',
                value: (<Status inActiveColor={colors.elements.statusOffline} activeText={statusStrings.statusNotSyncing} inactiveText={statusStrings.statusNotSyncing} active={false}/>),
            });
        }
        return arr;
    }, [
        account.isClosed,
        account.lastSyncAt,
        account.spaceId,
        colors.background.brandLight,
        colors.elements.brand,
        colors.elements.statusOffline,
        hasSpaces,
        isAccountLinkedToAutosave,
        isConnectionClosed,
        isConnectionDeactivated,
        rentReporting,
        spaceInfoRecord,
        styles.container,
        styles.iconCircle,
        styles.iconSpace,
    ]);
    const details = useMemo(() => {
        const { type, sortCode, accountNumber } = account;
        if (!sortCode && !accountNumber)
            return null;
        let accNumber = accountNumber;
        if (accountNumber?.length === 4) {
            if (type === 'CREDITCARD') {
                accNumber = `•••• •••• •••• ${accountNumber}`;
            }
            else {
                accNumber = `•••••• ${accountNumber}`;
            }
        }
        return (<BlurAmountView enabled={scrambled}>
        <Text TextThin-14 numberOfLines={1} Secondary>
          {formatSortCodeAndAccountNumber(sortCode, accNumber)}
        </Text>
      </BlurAmountView>);
    }, [account, scrambled]);
    const onManage = useCallback(() => {
        ManageAccountModal.show({
            isConnectionClosed,
            isAccountClosed: account.isClosed,
            isHidden: account.isHidden,
            toggleClosed,
            toggleHidden,
        });
    }, [account.isClosed, account.isHidden, isConnectionClosed, toggleClosed, toggleHidden]);
    return (<CardView noMarginTop noMarginBottom style={styles.card}>
      <View style={styles.name}>
        <LogoBankAccount size="R44" data={account}/>
        <View style={styles.flex}>
          <Text Text-16 numberOfLines={1}>
            {account.name}
          </Text>
          {details}
        </View>
      </View>
      <Separator style={styles.separator}/>
      {'isLoading' in account && account.isLoading ? (<ActivityIndicator size={rem(24)} color={colors.elements.secondary} style={styles.spinner}/>) : (<View style={[styles.content, isConnectionDeactivated && styles.contentDeactivated]}>
          {rows.map((row) => (<View key={row.title} style={styles.row}>
              <Text TextThin-14 Secondary>
                {row.title}
              </Text>
              {row.value}
            </View>))}
          {!isConnectionDeactivated && (<>
              <Spacer h={12}/>
              <Button square brandBorder title="Manage" onPress={onManage}/>
            </>)}
        </View>)}
    </CardView>);
};
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
    name: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(16),
        padding: rem(16),
    },
    separator: {
        backgroundColor: colors.borders.divider,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: rem(16),
        paddingVertical: rem(12),
    },
    syncIcon: {
        paddingRight: rem(8),
    },
    buttonContainer: {
        flexDirection: 'row',
        gap: rem(10),
    },
    content: {
        paddingTop: rem(4),
        paddingHorizontal: rem(16),
        paddingBottom: rem(16),
    },
    contentDeactivated: {
        paddingBottom: rem(4),
    },
    container: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    iconCircle: { width: rem(24), height: rem(24) },
    iconSpace: {
        borderColor: 'black',
    },
    spinner: {
        marginVertical: rem(24),
    },
}));
